import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import UniCard from "@/components/app/UniCard";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Search, BookOpen, Loader2 } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { engineKeyFromCountry, HOME_FEE_FALLBACK_USD } from "@/lib/countries";

// Explore pages through the whole filtered catalogue server-side, 50 cards at a time.
// 48 fills whole rows at 3 columns (desktop) and 2 (tablet).
const PAGE_SIZE = 48;

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [unis, setUnis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalMatching, setTotalMatching] = useState(0);
  // Read `?q=` off the URL so incoming deep-links (e.g. flagship-card
  // uni chips like `/explore?q=Harvard`) land pre-filtered. Without
  // this, the chip clicks fell into the full 58-uni catalogue and
  // felt broken.
  const [search, setSearch] = useState(params.get("q") || "");
  const [country, setCountry] = useState(params.get("country") || "all");
  const [countries, setCountries] = useState([]);
  const [budgetUsd, setBudgetUsd] = useState(120000);
  const [homeFeeOnly, setHomeFeeOnly] = useState(params.get("home_fee") === "1");
  const [feeStatusMap, setFeeStatusMap] = useState(null);  // engineKey → inference
  const [careerTarget, setCareerTarget] = useState(null);  // "Dentist" | null
  const { format } = useCurrency();

  // Fetch fee-status inference once per session so every card can show
  // its personalised pill and the "Home-fee eligible" filter has data
  // to work with. 401 (logged-out) is fine — map stays null and the
  // filter chip stays hidden.
  useEffect(() => {
    api.get("/fee-status/me")
      .then((r) => setFeeStatusMap(r.data?.by_country || null))
      .catch(() => setFeeStatusMap(null));
    // Pull the student's chosen destination so Explore can surface a
    // "pipelines to <X>" CTA. Silent-fail for logged-out callers.
    api.get("/profile")
      .then((r) => setCareerTarget((r.data?.career_target || "").trim() || null))
      .catch(() => setCareerTarget(null));
  }, []);

  // Country dropdown: fetch the distinct list once with per-country
  // counts so the picker always mirrors what's actually in the DB (was
  // hardcoded to UK/US/DE/FR — hid Ireland, NL, Italy, Finland, Spain,
  // Canada, Australia, UAE, Singapore, S Korea, Sweden, Denmark, HK,
  // Japan, Switzerland, Norway, India).
  useEffect(() => {
    api.get("/universities/countries").then((r) => setCountries(r.data || []));
  }, []);

  // Debounce the search box and budget slider so we don't fire an API call per keystroke / drag step.
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState(params.get("q") || "");
  const [debouncedBudget, setDebouncedBudget] = useState(budgetUsd);
  const [loadError, setLoadError] = useState(false);
  const [totalBeforeBudget, setTotalBeforeBudget] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(searchTimer.current);
  }, [search]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBudget(budgetUsd), 250);
    return () => clearTimeout(t);
  }, [budgetUsd]);

  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);
  const setPage = (n) => {
    const np = new URLSearchParams(params);
    if (n <= 1) np.delete("page"); else np.set("page", String(n));
    setParams(np);
  };
  // Home-fee filter runs server-side: send the stored country values where this student pays the home rate.
  const homeCountries = useMemo(() => {
    if (!homeFeeOnly || !feeStatusMap || countries.length === 0) return null;
    return countries.map((c) => c.country).filter((c) => {
      const key = engineKeyFromCountry(c);
      return !!key && feeStatusMap[key]?.status === "home";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeFeeOnly, feeStatusMap, countries]);
  const waitingForHomeCountries = homeFeeOnly && feeStatusMap && homeCountries === null;
  const filterKey = [country, debouncedSearch, debouncedBudget, homeCountries ? homeCountries.join("|") : "-"].join("~");
  const lastFilterKey = useRef(filterKey);
  const fetchSeq = useRef(0);

  // Server-side filtering, global sort and paging; a filter change goes back to page 1.
  useEffect(() => {
    if (waitingForHomeCountries) return;
    if (lastFilterKey.current !== filterKey) {
      lastFilterKey.current = filterKey;
      if (page !== 1) { setPage(1); return; }
    }
    const q = new URLSearchParams({ view: "card", limit: String(PAGE_SIZE), offset: String((page - 1) * PAGE_SIZE) });
    if (country !== "all") q.set("country", country);
    if (debouncedSearch) q.set("search", debouncedSearch);
    q.set("max_annual_cost_usd", String(debouncedBudget));
    if (homeCountries) q.set("countries_in", homeCountries.join("|"));
    const seq = ++fetchSeq.current;
    setLoading(true);
    setLoadError(false);
    api.get(`/universities?${q}`).then((r) => {
      if (seq !== fetchSeq.current) return;
      setUnis(r.data);
      const total = Number(r.headers?.["x-total-count"] ?? r.data.length);
      const before = Number(r.headers?.["x-total-before-budget"] ?? total);
      setTotalMatching(Number.isFinite(total) ? total : r.data.length);
      setTotalBeforeBudget(Number.isFinite(before) ? before : total);
    }).catch(() => {
      if (seq === fetchSeq.current) { setUnis([]); setLoadError(true); }
    }).finally(() => {
      if (seq === fetchSeq.current) setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page, waitingForHomeCountries, reloadTick]);

  const pageCount = Math.max(1, Math.ceil(totalMatching / PAGE_SIZE));
  const firstShown = totalMatching === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(totalMatching, (page - 1) * PAGE_SIZE + unis.length);
  const goToPage = (n) => {
    setPage(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Resolve the fee-status inference for one uni. Returns null when we
  // either don't model the country or the student isn't signed in —
  // callers treat null as "no personalisation available".
  const resolveFeeStatus = (uni) => {
    const key = engineKeyFromCountry(uni?.country);
    if (!key || !feeStatusMap) return null;
    const inf = feeStatusMap[key];
    if (!inf) return null;
    return {
      status: inf.status,
      reason: inf.reason,
      confidence: inf.confidence,
      home_fee_fallback_usd: HOME_FEE_FALLBACK_USD[key],
    };
  };

  const setCountryParam = (c) => {
    setCountry(c);
    const np = new URLSearchParams(params);
    if (c === "all") np.delete("country"); else np.set("country", c);
    setParams(np);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="label-uppercase">Explore</div>
      <h1 className="font-editorial text-4xl sm:text-5xl mt-2">All universities</h1>
      <p className="mt-3 text-stone-600 max-w-2xl">Filter by country, search by name, or cap by your annual budget (international-student total cost).</p>

      <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#4C1D95]/30 bg-[#F5F0FF] px-3 py-1.5 text-xs" data-testid="explore-cross-country-cta">
        <span className="font-underground-sc tracking-wider text-[10px] text-[#4C1D95]">New</span>
        <Link
          to="/explore/cross-country?subject=Computer%20Science"
          className="text-stone-900 hover:text-[#4C1D95] font-medium"
        >
          Compare a subject across UK · US · DE · FR · AU  →
        </Link>
      </div>

      <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-emerald-300/40 bg-emerald-50/60 px-3 py-1.5 text-xs" data-testid="explore-uk-fe-cta">
        <span className="font-underground-sc tracking-wider text-[10px] text-emerald-800">UK</span>
        <Link
          to="/uk-fe-colleges"
          className="text-stone-900 hover:text-emerald-800 font-medium"
        >
          The UK's 284 other HE routes — FE colleges, HNDs & top-ups  →
        </Link>
      </div>

      {careerTarget && (
        <div
          className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-300/60 bg-amber-50/60 px-3 py-1.5 text-xs"
          data-testid="explore-career-target-cta"
        >
          <span className="font-underground-sc tracking-wider text-[10px] text-amber-900">
            Your destination
          </span>
          <Link
            to="/advanced"
            className="text-stone-900 hover:text-amber-900 font-medium"
          >
            Pipelines to <span className="font-semibold">{careerTarget}</span> — rank unis by Fast Match →
          </Link>
        </div>
      )}

      <div className="mt-8 grid md:grid-cols-4 gap-6 border-y border-stone-200 py-6">
        <div className="md:col-span-2">
          <div className="label-uppercase mb-2">Search</div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              data-testid="explore-search-input"
              placeholder="Oxford, MIT, TUM…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div>
          <div className="label-uppercase mb-2">Country</div>
          <Select value={country} onValueChange={setCountryParam}>
            <SelectTrigger data-testid="explore-country-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All countries</SelectItem>
              {countries.map(({ country: c, count }) => (
                <SelectItem key={c} value={c} data-testid={`explore-country-opt-${c}`}>
                  {c}{" "}
                  <span className="text-stone-400 font-mono text-xs">
                    ({count.toLocaleString()})
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-uppercase mb-2">Max annual cost — {format(budgetUsd)}</div>
          <Slider
            data-testid="explore-budget-slider"
            value={[budgetUsd]}
            min={5000}
            max={120000}
            step={2500}
            onValueChange={(v) => setBudgetUsd(v[0])}
          />
        </div>
      </div>

      {/* Fee-status filter row — only renders when the student is
          signed in and has a profile the engine can read. For logged-out
          visitors the chip would do nothing useful, so we hide it. */}
      {feeStatusMap && (
        <div className="mt-4 space-y-1.5" data-testid="explore-fee-filters">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-stone-500">Fee status</span>
            <button
              type="button"
              onClick={() => {
                const next = !homeFeeOnly;
                setHomeFeeOnly(next);
                const np = new URLSearchParams(params);
                if (next) np.set("home_fee", "1"); else np.delete("home_fee");
                setParams(np);
              }}
              data-testid="explore-home-fee-chip"
              aria-pressed={homeFeeOnly}
              className={`text-[12px] px-3 py-1.5 rounded-full border transition ${
                homeFeeOnly
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-stone-700 border-stone-200 hover:border-emerald-400"
              }`}
            >
              {homeFeeOnly ? "✓ " : ""}Home-fee eligible only
            </button>
          </div>
          <p className="text-[11.5px] text-stone-500 leading-snug max-w-xl" data-testid="explore-home-fee-help">
            {homeFeeOnly
              ? "Showing only universities where your nationality + residency qualify you for the domestic fee rate. Costs on each card reflect the home rate, not the international."
              : "Toggle on to hide universities where you'd pay the international fee rate — leaves only destinations where your passport or residency unlocks the home rate."}
          </p>
        </div>
      )}

      <div className="mt-6 text-sm text-stone-500 font-mono" data-testid="explore-match-count">        {loading ? (
          <span
            className="inline-flex items-center gap-2"
            data-testid="explore-loading"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading universities…
          </span>
        ) : loadError ? (
          <span data-testid="explore-load-error">
            Couldn't load universities.{" "}
            <button type="button" className="underline" onClick={() => setReloadTick((n) => n + 1)} data-testid="explore-retry">Try again</button>
          </span>
        ) : (
          <>
            {totalMatching.toLocaleString()} of {totalBeforeBudget.toLocaleString()} universities match
            {totalBeforeBudget > totalMatching && (
              <span className="ml-2 text-stone-400" data-testid="explore-budget-hidden">
                · {(totalBeforeBudget - totalMatching).toLocaleString()} hidden by budget cap
              </span>
            )}
            {totalMatching > 0 && (
              <span className="ml-2 text-stone-400" data-testid="explore-range">
                · showing {firstShown.toLocaleString()}–{lastShown.toLocaleString()}
              </span>
            )}
          </>
        )}
      </div>

      <div className={`mt-6 explore-fill-grid transition-opacity ${loading && unis.length > 0 ? "opacity-50" : ""}`} data-testid="explore-grid">
        {loading && unis.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-56 rounded-xl border border-stone-200 bg-stone-100/60 animate-pulse"
              data-testid={`explore-skeleton-${i}`}
            />
          ))
        ) : (
          unis.map((u) => <UniCard key={u.id} uni={u} feeStatus={resolveFeeStatus(u)} />)
        )}
      </div>
      {!loading && !loadError && totalMatching === 0 && (
        <p className="mt-6 text-sm text-stone-500" data-testid="explore-empty">No universities match these filters.</p>
      )}
      {totalMatching > PAGE_SIZE && (
        <nav className="mt-8 flex items-center justify-between gap-4" aria-label="Universities pages" data-testid="explore-pager">
          <Button variant="outline" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)} data-testid="explore-prev">
            ← Previous
          </Button>
          <span className="text-sm text-stone-500 font-mono" data-testid="explore-page-indicator">
            Page {page} of {pageCount}
          </span>
          <Button variant="outline" disabled={page >= pageCount || loading} onClick={() => goToPage(page + 1)} data-testid="explore-next">
            Next →
          </Button>
        </nav>
      )}

      {/* -------- DIRECTORY COURSE SEARCH (opt-in) ---------------------
          Sprint B: user can toggle a "Show directory courses" pane below the
          uni grid. Search runs against 12,000+ HESA / Discover Uni stubs. */}
      <ExploreDirectorySearch />
    </div>
  );
}


function ExploreDirectorySearch() {
  const [enabled, setEnabled] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams({ directory_only: "true", limit: "24" });
      if (debouncedQ) params.set("title", debouncedQ);
      try {
        const { data, headers } = await api.get(`/degrees?${params}`);
        if (cancelled) return;
        setResults(data);
        setTotal(parseInt(headers["x-total-count"] || "0", 10));
      } catch { if (!cancelled) { setResults([]); setTotal(0); } }
    })();
    return () => { cancelled = true; };
  }, [enabled, debouncedQ]);

  return (
    <section className="mt-16 border-t border-stone-200 pt-10" data-testid="explore-directory-section">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase !text-[10px] text-stone-500">Sprint B · 12,000+ directory courses</div>
          <h2 className="font-editorial text-2xl mt-1">Show directory courses</h2>
          <p className="mt-1 text-sm text-stone-600 max-w-xl">
            Search across every UK undergraduate course (HESA / Discover Uni). Not yet
            editorially reviewed — no chance score or warnings — but linked to the
            university's page where you can request a full review.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-700">Off</span>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="explore-directory-toggle"
          />
          <span className="text-sm text-stone-700">On</span>
        </div>
      </div>

      {enabled && (
        <div className="mt-6" data-testid="explore-directory-panel">
          <div className="relative max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 12,000+ courses (e.g. Chemistry, Philosophy, Law)…"
              className="pl-9"
              data-testid="explore-directory-search-input"
            />
          </div>
          {total !== null && (
            <div className="mt-3 text-sm text-stone-500 font-mono" data-testid="explore-directory-count">
              {debouncedQ
                ? `${total.toLocaleString()} courses match "${debouncedQ}"`
                : `${total.toLocaleString()} courses across the UK directory`}
            </div>
          )}
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {results.map((r) => (
              <Link
                key={r.id}
                to={`/uni/${r.university_id}?open=directory`}
                data-testid={`explore-directory-card-${r.id}`}
                className="block p-3.5 border border-stone-200 rounded-md bg-white hover:border-[#4C1D95]/50 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <BookOpen className="h-3.5 w-3.5 text-stone-400 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-stone-900 truncate">{r.official_title}</div>
                    <div className="mt-0.5 text-[10px] text-stone-500 font-underground-sc tracking-wider truncate">
                      {r.qualification} · {r.duration_years || 3} yr · {r.university_id.replace(/^([a-z]{2})-/, "").replace(/-/g, " ")}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {total > results.length && (
            <p className="mt-4 text-[11px] text-stone-500">
              Showing {results.length} of {total.toLocaleString()}. Refine your search to narrow the list.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
